import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { toast } from '../components/ui/Toast';
import { api } from '../services/api';
import { useAuthStore } from '../stores/auth';

const registerSchema = z
  .object({
    email: z.string().email('Email non valida'),
    password: z
      .string()
      .min(8, 'La password deve avere almeno 8 caratteri')
      .regex(/[A-Z]/, 'La password deve contenere almeno una lettera maiuscola')
      .regex(/[a-z]/, 'La password deve contenere almeno una lettera minuscola')
      .regex(/[0-9]/, 'La password deve contenere almeno un numero'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Le password non corrispondono',
    path: ['confirmPassword'],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    try {
      const response = await api.register(data.email, data.password);
      login(response.user, response.accessToken, response.refreshToken);
      toast({ title: 'Registrazione completata', variant: 'success' });
      navigate('/');
    } catch (error) {
      toast({
        title: 'Errore durante la registrazione',
        description: 'Email gia registrata o errore del server',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-center px-6"
      style={{
        minHeight: 'calc(100dvh - var(--header-height))',
        backgroundColor: 'var(--color-surface-secondary)',
      }}
    >
      <div className="w-full max-w-sm">
        {/* Logo + Title */}
        <div className="text-center mb-8">
          <img
            src="/logo-icon.svg"
            alt="HomeFinder"
            className="mx-auto mb-4 h-14 w-14"
            style={{ borderRadius: 'var(--radius-md)' }}
          />
          <h1
            style={{
              fontSize: 'var(--font-size-2xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-text-primary)',
              letterSpacing: 'var(--letter-spacing-tight)',
            }}
          >
            Crea un account
          </h1>
          <p
            className="mt-1"
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-tertiary)',
            }}
          >
            Salva ricerche e ricevi notifiche
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label
              className="block mb-1.5"
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-text-secondary)',
              }}
            >
              Email
            </label>
            <Input
              type="email"
              placeholder="mario.rossi@email.it"
              autoComplete="email"
              error={!!errors.email}
              {...register('email')}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-error">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label
              className="block mb-1.5"
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-text-secondary)',
              }}
            >
              Password
            </label>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                autoComplete="new-password"
                error={!!errors.password}
                {...register('password')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--color-text-tertiary)' }}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-error">{errors.password.message}</p>
            )}
            <p
              className="mt-1"
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}
            >
              Min. 8 caratteri, una maiuscola, una minuscola e un numero
            </p>
          </div>

          <div>
            <label
              className="block mb-1.5"
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--color-text-secondary)',
              }}
            >
              Conferma password
            </label>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              autoComplete="new-password"
              error={!!errors.confirmPassword}
              {...register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-error">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Registrazione...' : 'Registrati'}
          </Button>
        </form>

        {/* Terms */}
        <p
          className="mt-4 text-center"
          style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}
        >
          Registrandoti accetti i{' '}
          <a href="/terms" style={{ color: 'var(--color-accent)' }}>
            Termini di servizio
          </a>{' '}
          e la{' '}
          <a href="/privacy" style={{ color: 'var(--color-accent)' }}>
            Privacy Policy
          </a>
        </p>

        {/* Footer link */}
        <p
          className="mt-6 text-center"
          style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-tertiary)' }}
        >
          Hai gia un account?{' '}
          <Link
            to="/login"
            style={{ color: 'var(--color-accent)', fontWeight: 'var(--font-weight-semibold)' }}
          >
            Accedi
          </Link>
        </p>
      </div>
    </div>
  );
}
